import { IQService, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalScope } from "angular-ui-bootstrap";
import { IUser, IInvitationRequest } from "../models";
import { UserService } from "../userService";
import { AccountService } from "../../accounts/accountsService";
import { IAccountSubscription } from "../../configuration/models";
import { AccountResolverService } from "../../state/accountResolverService";
import { IStateInit } from "@gtmhub/core/routing";
import { IAccount, ILicense } from "@gtmhub/core";
import { UIErrorHandlingService, createUIError } from "@gtmhub/error-handling";
import { CfMap } from "@gtmhub/customFields";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { hasCurrentUserRole } from "../current-user-storage";

interface IRequiredCustomField {
  requiredCfNotEmpty?: boolean;
}

export interface IInviteUsersScope extends IModalScope {
  accountLicenses: ILicense[];
  done: boolean;
  indicators: {
    loading?: boolean;
    sending?: boolean;
  };
  invitations: (IInvitationRequest & IRequiredCustomField)[];
  skip: {
    email: boolean;
  };
  isTrialAccount: boolean;
  isAdminUser: boolean;
  requiredCfNotEmpty: boolean;
  sendInvitation();
  addInvitation(): void;
  updateCustomFields(invitation: IInvitationRequest, customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }, index: number): void;
  removeInvitation(index: number): void;
}

export class InviteUsersCtrl implements IStateInit {
  public static $inject = ["$rootScope", "$scope", "$state", "UserService", "$q", "AccountService", "UIErrorHandlingService", "AccountResolverService"];

  constructor(
    private $rootScope: ITraceRootScopeService,
    private $scope: IInviteUsersScope,
    private $state: StateService,
    private userService: UserService,
    private $q: IQService,
    private accountService: AccountService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private accountResolverService: AccountResolverService
  ) {
    this.$scope.skip = {
      email: false,
    };

    this.$scope.indicators = {
      loading: true,
    };

    this.$scope.invitations = [angular.copy(this.defaultInvitation)];
    this.$scope.isTrialAccount = this.isTrialAccount(this.accountResolverService.getAccountData());
    this.$scope.isAdminUser = hasCurrentUserRole("admin");

    this.$scope.sendInvitation = this.sendInvitation;
    this.$scope.addInvitation = this.addInvitation;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.removeInvitation = this.removeInvitation;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    return this.getAccountSubscription();
  };

  private isTrialAccount = (account: IAccount): boolean => {
    return account.type === 0;
  };

  private strings = {
    loading: "loading",
  };

  private defaultInvitation: IInvitationRequest = {
    firstName: "",
    lastName: "",
    email: "",
    subscriptionType: "regular",
  };

  private sendInvitation = (): void => {
    this.$rootScope.traceAction("send_invitation", () => {
      this.$scope.indicators.sending = true;

      const userInvitePromises: IPromise<IUser>[] = [];

      for (let i = 0; i < this.$scope.invitations.length; i++) {
        if (this.$scope.invitations[i].status !== "sent") {
          this.$scope.invitations[i].status = "sending";

          const userInvitePromise = this.userService.createUser(this.$scope.invitations[i], this.$scope.skip.email);

          userInvitePromises.push(userInvitePromise);

          userInvitePromise.then(
            () => {
              this.$scope.invitations[i].status = "sent";
            },
            (rejection) => {
              this.$scope.invitations[i].status = "problem";

              const err = createUIError(rejection);
              if (err) {
                err.type =
                  err.body.indexOf("Sandbox Error: Internal server error") !== -1 || err.body.indexOf("Sandbox Error: Extensibility error") !== -1
                    ? "cannot_add_non_business_email_domain"
                    : err.type;

                this.$scope.invitations[i].error = err;
              }
            }
          );
        }
      }

      this.$q.all(userInvitePromises).then(
        (invitedUsers: IUser[]) => {
          this.$scope.indicators.sending = false;
          this.$rootScope.$broadcast("usersChanged", true);
          this.$rootScope.$broadcast("employeesChanged", true);
          this.$rootScope.$broadcast("invitedUsers", invitedUsers);

          // We want this behavior only for the home screen to navigate trial accounts to Settings -> Users
          const isHomeState = this.$state.current.name.includes(".home.");
          if (isHomeState) {
            this.$state.go("gtmhub.usersList");
          } else {
            this.$scope.done = true;
          }
        },
        () => {
          this.$scope.indicators.sending = false;
          this.$rootScope.$broadcast("usersChanged", true);
        }
      );
    });
  };

  // todo: in the feature this will be changed with another method, now we will use this one for testing
  private getAccountSubscription = (): IPromise<unknown> => {
    return this.accountService.getAccountSubscription().then(
      (accountSubscription: IAccountSubscription) => {
        this.$scope.indicators.loading = false;
        this.$scope.accountLicenses = accountSubscription.items;
        this.focusEmailElem(this.$scope.invitations);
      },
      (error) => this.handleIndicatorError(error, this.strings.loading, false)
    );
  };

  private addInvitation = (): void => {
    this.$scope.invitations.push(angular.copy(this.defaultInvitation));
    this.focusEmailElem(this.$scope.invitations);
  };

  private updateCustomFields = (invitation: IInvitationRequest, customFields: CfMap): void => {
    invitation.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }, index: number): void => {
    this.$scope.invitations[index].requiredCfNotEmpty = requiredCf.areFilled;
    this.$scope.requiredCfNotEmpty = this.$scope.invitations.every((invitation) => invitation.requiredCfNotEmpty);
  };

  private focusEmailElem = (invitations: IInvitationRequest[]): void => {
    const index = invitations.length - 1;
    setTimeout(() => document.getElementById(`input-email-${index}`).focus());
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private removeInvitation = (index: number): void => {
    this.setRequiredCf({ areFilled: true }, index);
    this.$scope.invitations.splice(index, 1);
  };
}
